import Vue from 'vue';
import i18n from '@/i18n';
import CombinationsConfiguratorTemplate from '@/modules/ConfiguratorCombinations/components/CombinationsConfiguratorTemplate.vue';
import { CombinationsConfigurationItem } from '@/modules/ConfiguratorCombinations/helpers/CombinationsConfiguration';
import {
    CanvasTemplateConfiguration,
    CombinationsConfiguratorFormData,
} from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import { IndividualGuideTypes } from '@/modules/ConfiguratorCombinations/composables/useGuideImages';

export interface CombinationCircleLetter {
    letter: string;
    visible: boolean;
}

export interface CombinationsCanvasRenderProps {
    productFormData: CombinationsConfiguratorFormData[];
    selectedRows: number[];
    guideType: string;
    secondGuideType: string;
    combinationType: string;
    isLastStep: boolean;
    widthDifference: number;
    combinedWidth: number;
    currentStep: number;
    currentConfig: CombinationsConfigurationItem;
    canvasTemplateConfiguration: CanvasTemplateConfiguration;
    configuratorType: string;
    disableDownloadButton: boolean;
    useSeparateBoxes: boolean;
    individualGuideTypes: IndividualGuideTypes;
    circleLetters: CombinationCircleLetter[][];
}

interface CanvasTemplateInstance extends Vue {
    prepareCanvasForCapture?: () => Promise<void>;
    captureAsPngBase64?: () => string | null;
}

function preloadGuideImages(configuration: CanvasTemplateConfiguration): Promise<void> {
    const imageSources = new Set<string>();

    for (const source of Object.values(configuration.guides)) {
        if (source) {
            imageSources.add(source);
        }
    }

    const imagePromises: Array<Promise<void>> = [];

    for (const source of imageSources) {
        imagePromises.push(
            new Promise((resolve) => {
                const image = new Image();
                image.onload = () => resolve();
                image.onerror = () => resolve();
                image.src = source;

                if (image.complete) {
                    resolve();
                }
            }),
        );
    }

    return Promise.all(imagePromises).then(() => undefined);
}

function waitForNextTick(): Promise<void> {
    return new Promise((resolve) => Vue.nextTick(resolve));
}

function waitForPaint(): Promise<void> {
    return new Promise((resolve) => {
        if (typeof window.requestAnimationFrame !== 'function') {
            window.setTimeout(resolve, 0);
            return;
        }

        window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
    });
}

export async function renderCombinationsCanvas(props: CombinationsCanvasRenderProps): Promise<string | null> {
    if (typeof document === 'undefined') {
        return null;
    }

    await preloadGuideImages(props.canvasTemplateConfiguration);

    const mountTarget = document.createElement('div');
    document.body.appendChild(mountTarget);

    const renderer = new Vue({
        i18n,
        render(createElement) {
            return createElement(
                'div',
                {
                    style: {
                        position: 'fixed',
                        left: '-100000px',
                        top: '0',
                        width: '2400px',
                        height: '1600px',
                        overflow: 'hidden',
                        pointerEvents: 'none',
                    },
                },
                [
                    createElement(CombinationsConfiguratorTemplate as any, {
                        ref: 'canvas',
                        props,
                    }),
                ],
            );
        },
    });

    try {
        renderer.$mount(mountTarget);
        await waitForNextTick();
        await waitForPaint();

        const canvasTemplate = renderer.$refs.canvas as CanvasTemplateInstance | undefined;
        await canvasTemplate?.prepareCanvasForCapture?.();
        await waitForPaint();

        return canvasTemplate?.captureAsPngBase64?.() ?? null;
    } finally {
        const rendererElement = renderer.$el;
        renderer.$destroy();

        if (rendererElement?.parentNode) {
            rendererElement.parentNode.removeChild(rendererElement);
        } else if (mountTarget.parentNode) {
            mountTarget.parentNode.removeChild(mountTarget);
        }
    }
}
